// VideoCommentCard.tsx — presentational comment card for the video watch page's
// comment thread. Owns only its like action (auth-gated). No edit/delete/sort —
// out of scope for video comments (owner decision). Visual language mirrors
// post-detail's CommentCard; deliberately its own component (no shared import).
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { css } from 'styled-components';
import type { VideoComment } from '@/core/models/videoComment';
import { useRequireAuth } from '@/hooks';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { useVideoCommentAuthor } from './useVideoCommentAuthor';

export interface VideoCommentCardProps {
  comment: VideoComment;
  onReply: (comment: VideoComment, authorHandle?: string | null) => void;
  onToggleLike: (id: string) => void;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarLink = styled(Link)`
  display: inline-flex;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const NameLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const HandleTime = styled.p`
  display: flex;
  column-gap: 0.9em;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const AttributionRow = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: 54px;
`;

const AttributionHandle = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
`;

const BodyText = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  margin: 0 0 0 54px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const baseButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 50px;
`;

const ActionBtn = styled.button`
  ${baseButtonStyle}
`;

const LikeBtn = styled(ActionBtn)<{ $active: boolean }>`
  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.color.accentMagenta};
      & svg {
        filter: drop-shadow(0 0 4px ${theme.color.accentMagenta});
      }
    `}
`;

export function VideoCommentCard({ comment, onReply, onToggleLike }: VideoCommentCardProps) {
  const requireAuth = useRequireAuth();
  const author = useVideoCommentAuthor(comment.authorId);
  const handleWithoutAt = (author?.handle ?? '').replace(/^@/, '');
  const profilePath = `/profile/${handleWithoutAt}`;

  function handleLike() {
    if (!requireAuth('cherish')) return;
    onToggleLike(comment.id);
  }

  function handleReply() {
    if (!requireAuth('reply')) return;
    onReply(comment, author?.handle ?? null);
  }

  return (
    <Root>
      <AuthorRow>
        <AvatarLink to={profilePath} aria-label={`${author?.name ?? comment.authorId}'s profile`}>
          <Avatar name={author?.name ?? comment.authorId} avatarKey={author?.av} size={44} />
        </AvatarLink>

        <MetaCol>
          <NameLine>
            <NameLink to={profilePath}>{author?.name ?? comment.authorId}</NameLink>
            {author?.verified && <Icon name="verify" size={14} title="Verified account" />}
          </NameLine>
          <HandleTime>
            {author?.handle && <span>{author.handle}</span>}
            <span>{comment.time}</span>
          </HandleTime>
        </MetaCol>
      </AuthorRow>

      {comment.replyingToHandle && (
        <AttributionRow>
          ↳ Replying to <AttributionHandle>{comment.replyingToHandle}</AttributionHandle>
        </AttributionRow>
      )}

      <BodyText>{comment.body}</BodyText>

      <ActionRow>
        <ActionBtn type="button" aria-label="Reply" onClick={handleReply}>
          <Icon name="reply" size={16} />
          Reply
        </ActionBtn>

        <LikeBtn
          type="button"
          aria-label={`${comment.liked ? 'Take back your Cherish' : 'Cherish comment'}, ${comment.likes} cherishes`}
          aria-pressed={comment.liked}
          $active={comment.liked}
          onClick={handleLike}
        >
          <Icon name="cherish" size={16} />
          {comment.likes}
        </LikeBtn>
      </ActionRow>
    </Root>
  );
}
